/**
 * Feedback Service
 * Handles feedback submission to the submit-feedback Edge Function
 */

// Get Supabase URL from environment or use default
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://dlmcxxzohervnbixauhs.supabase.co';

/**
 * Response type from submit-feedback Edge Function
 * @typedef {Object} FeedbackResponse
 * @property {boolean} success - Whether submission was successful
 * @property {string} [submission_id] - Unique submission ID
 * @property {Object} [meta_issue] - Meta repo issue info
 * @property {number} [meta_issue.number] - Issue number
 * @property {string} [meta_issue.url] - Issue URL
 * @property {Object} [project_issue] - Project repo issue info
 * @property {number} [project_issue.number] - Issue number
 * @property {string} [project_issue.url] - Issue URL
 * @property {string} [project_issue.repo] - Repository name
 * @property {string} [message] - Success message
 * @property {string} [error] - Error message if failed
 * @property {string} [details] - Error details if failed
 */

/**
 * Submit feedback to the backend
 * @param {Object} feedbackData - The feedback data to submit
 * @param {string} feedbackData.center_id - Center UUID
 * @param {string} feedbackData.center_name - Center name
 * @param {string} [feedbackData.center_code] - Center code
 * @param {string} [feedbackData.user_name] - User name (optional)
 * @param {string} [feedbackData.user_email] - User email (optional)
 * @param {string} feedbackData.section - Section being rated
 * @param {Object} feedbackData.ratings - Rating values by category
 * @param {Object} feedbackData.comments - Comment values by section
 * @param {string} [feedbackData.improvement_suggestions] - General suggestions
 * @param {string} [feedbackData.additional_comments] - Additional comments
 * @returns {Promise<FeedbackResponse>} The response from the server
 */
export const submitFeedback = async (feedbackData) => {
  try {
    const url = `${SUPABASE_URL}/functions/v1/submit-feedback`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(feedbackData),
    });

    const data = await response.json();

    if (!response.ok) {
      // Handle HTTP errors
      throw new FeedbackError(
        data.error || `Request failed with status ${response.status}`,
        response.status,
        data.details
      );
    }

    return data;
  } catch (error) {
    // Re-throw FeedbackErrors as-is
    if (error instanceof FeedbackError) {
      throw error;
    }

    // Handle network errors
    if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      throw new FeedbackError(
        'Unable to connect to the server. Please check your internet connection.',
        0,
        'Network error'
      );
    }

    // Handle other errors
    throw new FeedbackError(
      error.message || 'An unexpected error occurred',
      500,
      error.stack
    );
  }
};

/**
 * Custom error class for feedback-related errors
 */
export class FeedbackError extends Error {
  /**
   * Create a FeedbackError
   * @param {string} message - Error message
   * @param {number} status - HTTP status code (0 for network errors)
   * @param {string} [details] - Additional error details
   */
  constructor(message, status, details) {
    super(message);
    this.name = 'FeedbackError';
    this.status = status;
    this.details = details;
  }
}

/**
 * Format ratings for submission (filter out zero values)
 * @param {Object} ratings - Raw ratings object
 * @returns {Object} Filtered ratings with only non-zero values
 */
export const formatRatingsForSubmission = (ratings) => {
  const formatted = {};
  for (const [key, value] of Object.entries(ratings)) {
    if (value > 0) {
      formatted[key] = value;
    }
  }
  return formatted;
};

/**
 * Format comments for submission (filter out empty values)
 * @param {Object} comments - Raw comments object
 * @returns {Object} Filtered comments with only non-empty values
 */
export const formatCommentsForSubmission = (comments) => {
  const formatted = {};
  for (const [key, value] of Object.entries(comments)) {
    if (value && value.trim().length > 0) {
      formatted[key] = value.trim();
    }
  }
  return formatted;
};

/**
 * Helper to determine the primary section based on ratings/comments
 * @param {Object} feedbackData - The feedback data
 * @returns {string} The primary section identifier
 */
export const determinePrimarySection = (feedbackData) => {
  const { ratings, comments } = feedbackData;

  // Check which sections have the most engagement
  const sectionScores = {
    lectures: 0,
    'video-player': 0,
    announcements: 0,
    calendar: 0,
    publications: 0,
    general: 0,
  };

  // Score based on ratings
  if (ratings.lectures_transcription || ratings.lectures_summary ||
      ratings.lectures_notes || ratings.lectures_takeaways) {
    sectionScores.lectures += 2;
  }

  if (ratings.video_playback || ratings.shorts_experience) {
    sectionScores['video-player'] += 2;
  }

  if (ratings.announcements_usefulness || ratings.announcements_clarity) {
    sectionScores.announcements += 2;
  }

  if (ratings.calendar_usability || ratings.calendar_completeness) {
    sectionScores.calendar += 2;
  }

  if (ratings.publications_quality || ratings.publications_accessibility) {
    sectionScores.publications += 2;
  }

  // Score based on comments
  if (comments.lectures) sectionScores.lectures += 1;
  if (comments.video) sectionScores['video-player'] += 1;
  if (comments.announcements) sectionScores.announcements += 1;
  if (comments.calendar) sectionScores.calendar += 1;
  if (comments.publications) sectionScores.publications += 1;

  // Find the section with highest score
  let maxScore = 0;
  let primarySection = 'general';

  for (const [section, score] of Object.entries(sectionScores)) {
    if (score > maxScore) {
      maxScore = score;
      primarySection = section;
    }
  }

  return primarySection;
};

const feedbackService = {
  submitFeedback,
  formatRatingsForSubmission,
  formatCommentsForSubmission,
  determinePrimarySection,
  FeedbackError,
};

export default feedbackService;
